import { useEffect, useMemo, useState } from "react";
import { useQuery } from "react-query";
import { apiConfig } from "../config/apiConfig";
import { Periods, RideStatus } from "../helpers/enums";
import apiService from "../service/apiService";
import { useCookie } from "./useCookie";
import { getDateRange } from "../helpers/general";

type Query = {
  query?: string;
  page?: number | undefined;
  status?: RideStatus;
  paymentMethod?: "cash" | "user_balance";
  isValidRide?: string;
  payed?: string;
  couponPaid?: string;
  userBalancePaid?: string;
  driverId?: string;
  period?: Periods;
  startDate?: string;
  endDate?: string;
  fetchAll?: boolean;
  quickFilter?: "bonus" | "coupon" | "balance" | null;
};

type RideResponse = {
  items: Ride[];
  meta: PaginationMeta;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type UnpaidRidesResponse = {
  currentCount: number;
  requiredAmount: number;
  bonus: number;
};

type UnpaidCountsResponse = {
  bonusUnpaid: number;
  couponUnpaid: number;
  balanceUnpaid: number;
};

const defaultQuery = {
  query: "",
  page: 1, // ✅ Start at 1, not 4
  period: undefined,
  fetchAll: false,
  quickFilter: null,
};

export function useRides(defaultQueries?: Partial<Query>) {
  const cleanObject = <T extends Record<string, any>>(obj: T): T => {
    return Object.entries(obj).reduce((acc, [key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        acc[key as keyof T] = value;
      }
      return acc;
    }, {} as T);
  };

  const [queries, setQueries] = useState<Query>(() => {
    const initial = { ...defaultQuery, ...defaultQueries };
    if (defaultQueries?.page) {
      initial.page = defaultQueries.page;
    }
    return cleanObject(initial);
  });

  useEffect(() => {
    if (defaultQueries) {
      setQueries((prev) => {
        const { page: prevPage, ...restPrev } = prev;
        const { page: newPage, ...restNew } = defaultQueries;
        const updatedQueries = {
          ...restPrev,
          ...restNew,
          page: newPage || prevPage,
        };
        if (newPage !== undefined && newPage !== null) {
          updatedQueries.page = Number(newPage);
        } else if (prevPage !== undefined) {
          updatedQueries.page = prevPage;
        }
        return cleanObject(updatedQueries);
      });
    }
  }, [defaultQueries]);

  function formatDate(date: Date): string {
    return date.toISOString().replace(/\.\d{3}Z$/, "Z");
  }

  const getDatesFromPeriod = () => {
    if (!queries.period) return { startDate: "", endDate: "" };
    const [start, end] = getDateRange(queries.period);
    return { startDate: formatDate(start), endDate: formatDate(end) };
  };

  const { startDate, endDate } = useMemo(() => {
    if (queries.startDate && queries.endDate) {
      return { startDate: queries.startDate, endDate: queries.endDate };
    }
    return getDatesFromPeriod();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.period, queries.startDate, queries.endDate]);

  const endpoint = apiConfig.rides.all({
    ...queries,
    startDate,
    endDate,
  } as Record<string, any>);

  const { token } = useCookie("vToken");

  const {
    query,
    page,
    status,
    paymentMethod,
    isValidRide,
    payed,
    driverId,
    couponPaid,
    userBalancePaid,
  } = queries;

  const deps = [
    "rides",
    query,
    page,
    status,
    paymentMethod,
    startDate,
    endDate,
    isValidRide,
    payed,
    driverId,
    endpoint,
    couponPaid,
    userBalancePaid,
    queries.fetchAll,
  ];

  const { data, isLoading } = useQuery<RideResponse>(deps, () => {
    return apiService.MakeGetRequest(endpoint, token);
  });

  const { data: unpaidCounts } = useQuery<UnpaidCountsResponse>(
    ["unpaidRidesCounts", queries.driverId],
    () =>
      apiService.MakeGetRequest(
        `rides/unpaid-counts?driverId=${queries.driverId || ""}`,
        token,
      ),
    { enabled: queries.driverId !== undefined },
  );

  const addQuery = <K extends keyof Query>(key: K, value: Query[K]) => {
    setQueries((prev) => {
      const updated = { ...prev, [key]: value };
      // Optional: reset page when filter changes
      // if (key !== "page") updated.page = 1;
      return cleanObject(updated);
    });
  };

  // const setDates = (dates: { from: string; to: string }) => {
  //   setQueries((prev) => ({ ...prev, page: 1 }));
  // };

  const setDates = (dates: { from: string; to: string }) => {
    setQueries((prev) =>
      cleanObject({
        ...prev,
        startDate: dates.from,
        endDate: dates.to,
        page: 1,
      }),
    );
  };

  const removeRide = async (rideId: string) => {};

  // ✅ Clear quick filter
  const clearQuickFilter = () => {
    setQueries((prev) =>
      cleanObject({
        ...prev,
        fetchAll: false,
        quickFilter: null,
        page: 1,

        payed: undefined,
        couponPaid: undefined,
        userBalancePaid: undefined,
        isValidRide: undefined,
        paymentMethod: undefined,
        startDate: undefined,
        endDate: undefined,
      }),
    );
  };

  const handleGetUnpaidBonus = () => {
    if (!unpaidCounts?.bonusUnpaid) return;

    // ✅ Single state update: resets all filters & sets new ones
    setQueries((prev) =>
      cleanObject({
        driverId: prev.driverId,

        page: 1,
        fetchAll: true,

        quickFilter: "bonus",

        payed: "false",
        isValidRide: "true",

        status: undefined,
        query: undefined,
        paymentMethod: undefined,
        period: undefined,
        startDate: undefined,
        endDate: undefined,
        couponPaid: "",
        userBalancePaid: undefined,
      }),
    );
  };

  const handleGetUnpaidCoupon = () => {
    if (!unpaidCounts?.couponUnpaid) return;

    // ✅ Single state update: resets all filters & sets new ones
    setQueries((prev) =>
      cleanObject({
        driverId: prev.driverId,

        page: 1,
        fetchAll: true,

        quickFilter: "coupon",

        couponPaid: "false",
        isValidRide: "true",

        status: undefined,
        query: undefined,
        paymentMethod: undefined,
        period: undefined,
        startDate: undefined,
        endDate: undefined,
        userBalancePaid: undefined,
        payed: undefined,
      }),
    );
  };

  const handleGetUnpaidBalance = () => {
    if (!unpaidCounts?.balanceUnpaid) return;

    // ✅ Single state update: resets all filters & sets new ones
    setQueries((prev) =>
      cleanObject({
        // 1. Keep essential context
        driverId: prev.driverId,

        // 2. Set pagination & fetch mode
        page: 1,
        fetchAll: true,

        // 3. Set quick filter state
        quickFilter: "balance",

        // 4. Apply specific filters for this view
        userBalancePaid: "false",
        isValidRide: "true",

        // 5. Explicitly reset other filters (cleanObject will strip them anyway)
        status: undefined,
        query: undefined,
        paymentMethod: undefined,
        period: undefined,
        startDate: undefined,
        endDate: undefined,
        couponPaid: undefined,
        payed: undefined,
      }),
    );
  };

  return {
    rides: data,
    isLoading,
    queries,
    addQuery,
    removeRide,
    setDates,
    unpaidCounts,
    clearQuickFilter,
    isQuickFilterActive: !!queries.quickFilter,
    activeQuickFilter: queries.quickFilter,
    handleGetUnpaidBonus,
    handleGetUnpaidCoupon,
    handleGetUnpaidBalance,
  };
}
